import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { ChevronRight, ChevronLeft, Loader2, AlertTriangle, Truck, Store, Coffee, Utensils, ShoppingBag, CookingPot, Catering, MoreHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const BUSINESS_TYPES = [
  { id: "food_truck", label: "Food Truck", icon: Truck },
  { id: "restaurant", label: "Restaurant", icon: Utensils },
  { id: "bakery", label: "Bakery", icon: CookingPot },
  { id: "cafe", label: "Cafe", icon: Coffee },
  { id: "bar", label: "Bar", icon: Store },
  { id: "food_cart", label: "Food Cart", icon: ShoppingBag },
  { id: "catering", label: "Catering", icon: Utensils },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

const VENDOR_CHANNELS = [
  { id: "word_of_mouth", label: "Word of Mouth" },
  { id: "another_vendor", label: "Another Vendor" },
  { id: "google", label: "Google" },
  { id: "social_media", label: "Social Media" },
  { id: "our_team", label: "Our Team Contacted You" },
  { id: "event", label: "At an Event or Market" },
];

const STEPS = [
  { id: "business_type", title: "What kind of business?", subtitle: "This determines which features you'll see" },
  { id: "truck_count", title: "How many trucks do you operate?", subtitle: "Select the number of trucks in your fleet" },
  { id: "business_name", title: "What's your business name?", subtitle: "We may already have your listing" },
  { id: "city", title: "Where do you operate?", subtitle: "Your primary service area" },
  { id: "birthday_program", title: "The Birthday Program", subtitle: "This is the core of BirthdayDeaLight" },
  { id: "leaderboard", title: "Join the Vendor Leaderboard?", subtitle: "See how your truck ranks among similar trucks" },
  { id: "external_links", title: "External Links", subtitle: "Help customers find you beyond BirthdayDeaLight" },
  { id: "google_business_profile", title: "Claim Your Google Business Profile", subtitle: "A claimed profile means your hours, photos, and birthday offers show up correctly on Google Maps and Google Search." },
  { id: "age_restricted_offers", title: "Age-Restricted Offers", subtitle: "Important information about age-restricted content" },
  { id: "channel", title: "How did you find us?", subtitle: "Select all that apply" },
];

const PORTFOLIO_COLORS = [
  "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#EF4444",
  "#14B8A6", "#F97316", "#EC4899", "#6366F1", "#06B6D4",
];

export default function VendorOnboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form state
  const [businessType, setBusinessType] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [cityInput, setCityInput] = useState("");
  const [birthdayChoice, setBirthdayChoice] = useState(null); // "yes" | "no"
  const [showFinalWarning, setShowFinalWarning] = useState(false);
  const [channels, setChannels] = useState([]);
  const [googleBusinessClaimed, setGoogleBusinessClaimed] = useState(false);
  const [leaderboardOpt, setLeaderboardOpt] = useState(true);
  const [appStoreUrl, setAppStoreUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [truckCount, setTruckCount] = useState(1);
  const [portfolioTrucks, setPortfolioTrucks] = useState([]);
  const [portfolioStep, setPortfolioStep] = useState(0); // which truck form we're on
  const isPortfolio = truckCount >= 2;

  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFoodTruck = businessType === "food_truck" || businessType === "food_cart";

  const addCity = () => {
    const trimmed = cityInput.trim();
    if (trimmed && !cities.includes(trimmed)) {
      setCities(prev => [...prev, trimmed]);
      setCityInput("");
    }
  };

  const removeCity = (c) => setCities(prev => prev.filter(x => x !== c));
  const toggleChannel = (id) => setChannels(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const handleNext = () => {
    if (currentStep.id === "business_type" && !businessType) {
      toast.error("Please select your business type");
      return;
    }
    if (currentStep.id === "truck_count") {
      // Initialize portfolio truck forms if multi-truck
      if (truckCount >= 2 && portfolioTrucks.length === 0) {
        const forms = Array.from({ length: truckCount }, (_, i) => ({
          name: i === 0 ? businessName : "",
          cuisine_tags: "",
          location: i === 0 && cities.length > 0 ? cities[0] : "",
          description: "",
          birthday_offer: "",
        }));
        setPortfolioTrucks(forms);
      }
    }
    if (currentStep.id === "business_name" && !businessName.trim()) {
      toast.error("Please enter your business name");
      return;
    }
    if (currentStep.id === "birthday_program" && birthdayChoice === null) {
      toast.error("Please make a choice about the birthday program");
      return;
    }
    if (currentStep.id === "birthday_program" && birthdayChoice === "no") {
      setShowFinalWarning(true);
      return;
    }
    if (isLast) {
      handleComplete();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleDeclineConfirm = async () => {
    // User confirmed they don't want vendor mode — convert to user
    setSaving(true);
    try {
      // Save what we have
      await axios.post(`${API}/auth/onboarding-preferences`, {
        acquisition_channel: channels,
        city: city || (cities.length > 0 ? cities[0] : undefined),
      });
      // Reset to user path
      await axios.patch(`${API}/auth/me`, {
        onboarding_path: "user",
        role: "user",
        onboarding_complete: true,
      });
      if (refreshUser) await refreshUser();
      navigate("/");
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/onboarding-preferences`, {
        acquisition_channel: channels,
        city: city || (cities.length > 0 ? cities[0] : undefined),
      });

      await axios.patch(`${API}/auth/me`, {
        business_type: businessType,
        business_name: businessName,
        service_cities: cities.length > 0 ? cities : (city ? [city] : []),
        birthday_program_participant: true,
        onboarding_complete: true,
        truck_count: truckCount,
        google_business_claimed: googleBusinessClaimed,
      });

      if (refreshUser) await refreshUser();

      try {
        await axios.post(`${API}/vendor/leaderboard-opt`, { opted_in: leaderboardOpt });
      } catch {}

      if (appStoreUrl.trim() || websiteUrl.trim()) {
        try {
          await axios.put(`${API}/vendor/external-links`, {
            app_store_url: appStoreUrl.trim() || null,
            website_url: websiteUrl.trim() || null,
          });
        } catch {}
      }

      // Create portfolio if multi-truck
      if (isPortfolio && portfolioTrucks.length >= 2) {
        try {
          // For portfolio, use business name as portfolio name and truck names from forms
          const truckIds = portfolioTrucks.map((_, i) => `TRUCK-${Date.now()}-${i}`);
          await axios.post(`${API}/vendor/portfolio/create`, {
            portfolio_name: businessName,
            truck_business_ids: truckIds,
          });
        } catch {}
      }

      navigate("/vendor/dashboard");
    } catch (e) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Final warning modal
  if (showFinalWarning) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-white rounded-2xl border border-amber-200 p-6 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-800">Before you go</h2>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            If you continue as a regular user, the vendor option will disappear from your account. 
            To get vendor access later, you would need to request authorization from our team.
          </p>

          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            We'd love to have your business in the BirthdayDeaLight program — birthday celebrants 
            bring friends, family, and coworkers who all pay full price. A single free birthday 
            item brings you a table of paying customers you would never have found on your own.
          </p>

          <div className="space-y-2">
            <button
              onClick={() => { setShowFinalWarning(false); setBirthdayChoice("yes"); }}
              className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 transition"
              data-testid="reconsider-btn"
            >
              Wait, let me reconsider
            </button>
            <button
              onClick={handleDeclineConfirm}
              disabled={saving}
              className="w-full py-3 rounded-xl bg-slate-100 text-slate-500 text-sm hover:bg-slate-200 transition flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="confirm-user-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Yes, I'll use the app as a regular user
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Progress bar */}
        <div className="flex gap-1.5 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 rounded-full flex-1 transition-colors ${i <= step ? "bg-amber-500" : "bg-slate-200"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
          >
            <h2 className="text-xl font-bold text-slate-800" data-testid={`vonboard-title-${currentStep.id}`}>{currentStep.title}</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">{currentStep.subtitle}</p>

            {/* Step 1: Business Type */}
            {currentStep.id === "business_type" && (
              <div className="grid grid-cols-2 gap-2" data-testid="vonboard-business-type">
                {BUSINESS_TYPES.map(bt => {
                  const Icon = bt.icon;
                  return (
                    <button
                      key={bt.id}
                      onClick={() => setBusinessType(bt.id)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        businessType === bt.id
                          ? "bg-amber-500 border-amber-500 text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                      }`}
                      data-testid={`btype-${bt.id}`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${businessType === bt.id ? "text-white" : "text-amber-500"}`} />
                      <p className="text-sm font-medium">{bt.label}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Step: Truck Count */}
            {currentStep.id === "truck_count" && (
              <div data-testid="vonboard-truck-count">
                <p className="text-xs text-slate-500 mb-4">Tap to select how many trucks you operate</p>
                <div className="grid grid-cols-5 gap-2">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <button
                      key={n}
                      onClick={() => setTruckCount(n)}
                      className={`p-3 rounded-xl border text-center font-bold transition-all ${
                        truckCount === n
                          ? "bg-amber-500 border-amber-500 text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                      }`}
                      data-testid={`truck-count-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setTruckCount(11)}
                  className={`mt-2 w-full p-3 rounded-xl border text-center font-bold transition-all ${
                    truckCount >= 11
                      ? "bg-amber-500 border-amber-500 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                  }`}
                  data-testid="truck-count-10plus"
                >
                  10+
                </button>
                {truckCount >= 2 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-sm font-medium text-amber-700">Multi-truck detected</p>
                    <p className="text-xs text-amber-600 mt-1">You'll set up a portfolio with individual profiles for each truck.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step: Business Name */}
            {currentStep.id === "business_name" && (
              <div data-testid="vonboard-business-name">
                <input
                  type="text"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  placeholder="e.g. Taco Express, Main Street Bakery"
                  className="w-full p-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-lg"
                  autoFocus
                  data-testid="business-name-input"
                />
                <p className="text-xs text-slate-400 mt-2">If we already have your listing, we'll match you automatically.</p>
              </div>
            )}

            {/* Step: City / Service Area */}
            {currentStep.id === "city" && (
              <div data-testid="vonboard-city">
                {isFoodTruck ? (
                  <>
                    <p className="text-xs text-slate-500 mb-2">Add all cities where you operate</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={cityInput}
                        onChange={e => setCityInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addCity()}
                        placeholder="Add a city"
                        className="flex-1 p-3 rounded-xl border border-slate-200 bg-white text-slate-800"
                        data-testid="city-multi-input"
                      />
                      <button
                        onClick={addCity}
                        className="px-4 rounded-xl bg-amber-500 text-white font-bold text-sm"
                        data-testid="add-city-btn"
                      >
                        Add
                      </button>
                    </div>
                    {cities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {cities.map(c => (
                          <span key={c} className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm flex items-center gap-1">
                            {c}
                            <button onClick={() => removeCity(c)} className="ml-1 text-amber-500 hover:text-amber-800">&times;</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    placeholder="Sacramento, CA"
                    className="w-full p-3 rounded-xl border border-slate-200 bg-white text-slate-800"
                    data-testid="city-single-input"
                  />
                )}
              </div>
            )}

            {/* Step: Birthday Program Gate */}
            {currentStep.id === "birthday_program" && (
              <div data-testid="vonboard-birthday-program">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    BirthdayDeaLight exists to connect birthday celebrants with local businesses 
                    offering birthday specials. When someone celebrates their birthday, they rarely 
                    come alone — they bring friends, family, and coworkers who all pay full price.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mt-3">
                    A free birthday offer costs you one item. The group it brings you pays full price. 
                    Joining the birthday program puts your business in front of groups who are actively 
                    looking for places to celebrate.
                  </p>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => setBirthdayChoice("yes")}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      birthdayChoice === "yes"
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                    }`}
                    data-testid="birthday-yes-btn"
                  >
                    <p className="font-bold text-sm">Sign me up for the birthday program</p>
                    <p className={`text-xs mt-0.5 ${birthdayChoice === "yes" ? "text-amber-100" : "text-slate-400"}`}>
                      I want birthday celebrants to discover my business
                    </p>
                  </button>

                  <button
                    onClick={() => setBirthdayChoice("no")}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      birthdayChoice === "no"
                        ? "bg-slate-200 border-slate-300 text-slate-700"
                        : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                    data-testid="birthday-no-btn"
                  >
                    <p className="font-medium text-sm">Not right now</p>
                    <p className="text-xs mt-0.5">I'd rather use the app as a regular user</p>
                  </button>
                </div>
              </div>
            )}

            {/* Step: Leaderboard Opt-in */}
            {currentStep.id === "leaderboard" && (
              <div data-testid="vonboard-leaderboard">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    See how your truck ranks among similar trucks in your city. Monthly rankings are
                    public — users see your badges on your profile. Opt out anytime in Settings.
                  </p>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => setLeaderboardOpt(true)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      leaderboardOpt
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                    }`}
                    data-testid="leaderboard-yes-btn"
                  >
                    <p className="font-bold text-sm">Yes — Join the Leaderboard</p>
                  </button>

                  <button
                    onClick={() => setLeaderboardOpt(false)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      !leaderboardOpt
                        ? "bg-slate-200 border-slate-300 text-slate-700"
                        : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                    data-testid="leaderboard-no-btn"
                  >
                    <p className="font-medium text-sm">No thanks — keep my performance private</p>
                  </button>
                </div>
                <button onClick={() => { setLeaderboardOpt(true); setStep(s => s + 1); }}
                  className="mt-3 text-xs text-slate-400 underline w-full text-center" data-testid="leaderboard-skip">
                  Skip
                </button>
              </div>
            )}

            {/* Step: External Links */}
            {currentStep.id === "external_links" && (
              <div data-testid="vonboard-external-links" className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">App Store Link</label>
                  <input
                    value={appStoreUrl}
                    onChange={e => setAppStoreUrl(e.target.value)}
                    placeholder="Paste your Google Play or App Store URL if you have an app"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100"
                    data-testid="onboard-app-url"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">This is optional. You can add or change this anytime in Settings.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Website URL</label>
                  <input
                    value={websiteUrl}
                    onChange={e => setWebsiteUrl(e.target.value)}
                    placeholder="www.yourtruck.com"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100"
                    data-testid="onboard-website-url"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">This is optional. You can add or change this anytime in Settings.</p>
                </div>
                <button
                  onClick={() => setStep(s => s + 1)}
                  className="w-full py-3 rounded-xl bg-slate-100 text-slate-500 text-sm font-medium hover:bg-slate-200 transition"
                  data-testid="external-links-skip"
                >
                  Skip
                </button>
              </div>
            )}

            {/* Step: Google Business Profile — v1.0.0 Gate 6 Item 1 */}
            {currentStep.id === "google_business_profile" && (
              <div data-testid="vonboard-gbp" className="space-y-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    <span className="font-bold">Why it matters:</span> A claimed profile means your hours, photos, and birthday offers show up correctly on Google Maps and Google Search.
                  </p>
                </div>
                <ol className="space-y-2.5" data-testid="vonboard-gbp-steps">
                  {[
                    "Search your business on Google Maps",
                    "Claim the listing",
                    "Verify ownership",
                    "Your offers become discoverable",
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm text-slate-700 leading-relaxed">{text}</p>
                    </li>
                  ))}
                </ol>
                <label className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-white cursor-pointer" data-testid="vonboard-gbp-claimed-label">
                  <input
                    type="checkbox"
                    checked={googleBusinessClaimed}
                    onChange={(e) => setGoogleBusinessClaimed(e.target.checked)}
                    className="w-4 h-4 text-blue-500 rounded"
                    data-testid="vonboard-gbp-claimed-checkbox"
                  />
                  <span className="text-xs font-medium text-slate-700">My Google Business Profile is already claimed</span>
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStep(s => s + 1)}
                    className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 text-sm font-semibold"
                    data-testid="vonboard-gbp-later-btn"
                  >
                    I'll do this later
                  </button>
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(businessName || "my business")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-3 rounded-xl bg-blue-500 text-white text-sm font-semibold text-center"
                    data-testid="vonboard-gbp-open-btn"
                  >
                    Open Google Maps
                  </a>
                </div>
              </div>
            )}

            {/* Step: Acquisition Channel (multi-select) */}
            {currentStep.id === "age_restricted_offers" && (
              <div data-testid="vonboard-age-restriction" className="space-y-4">
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    If your business serves alcohol, cannabis, tobacco, or other age-restricted products, your birthday offers will only be shown to users who meet the legal age requirement.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mt-3">
                    This happens automatically based on your business category. No additional action needed.
                  </p>
                </div>
                <p className="text-xs text-slate-400 text-center">Tap Continue to proceed.</p>
              </div>
            )}

            {/* Step: Acquisition Channel (multi-select) */}
            {currentStep.id === "channel" && (
              <div className="space-y-2" data-testid="vonboard-channels">
                {VENDOR_CHANNELS.map(ch => (
                  <button
                    key={ch.id}
                    onClick={() => toggleChannel(ch.id)}
                    className={`w-full p-3 rounded-xl border text-sm font-medium text-left transition-all ${
                      channels.includes(ch.id)
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"
                    }`}
                    data-testid={`vchannel-${ch.id}`}
                  >
                    {ch.label}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            data-testid="vonboard-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />Back
          </button>

          <button
            onClick={handleNext}
            disabled={saving}
            className="px-6 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition flex items-center gap-1 disabled:opacity-50"
            data-testid="vonboard-next-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isLast ? "Let's Go" : "Next"}
            {!isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
